import axios from 'axios';
import { size, map, isEmpty, get } from 'lodash';
import cuid from 'cuid';

class MemoryStore {
  constructor() {
    this.store = {};
  }

  getItem(key) {
    return this.store[key] || null;
  }

  setItem(key, value) {
    this.store[key] = value;
    return value;
  }

  removeItem(key) {
    delete this.store[key];
  }

  clear() {
    this.store = {};
  }

  length() {
    return size(this.store);
  }

  iterate(fn) {
    return Promise.all(map(this.store, (value, key) => fn(value, key)));
  }

}

function responseSuccessInterceptor(response) {
  const config = response.config;

  if (config.method.toLowerCase() === 'get' && isEmpty(config.params)) {
    // check if in cache
    store.removeItem(config.url);
  }

  return response;
}

function responseErrorInterceptor(error) {
  const config = error.config;

  if (config.method.toLowerCase() === 'get' && isEmpty(config.params)) {
    // check if in cache
    store.removeItem(config.url);
  }

  return Promise.reject(error);
}

const store = new MemoryStore();

const HEADERS_TOKEN = 'token';
const HEADERS_ACCEPT = 'accept';
const HEADERS_APPLICATION = 'application';
const HEADERS_LANGUAGE = 'language'; //TODO: error response handler

axios.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);
const cachedAxios = config => {
  if (config.method.toLowerCase() === 'get' && isEmpty(config.params)) {
    // check if in cache
    const promise = store.getItem(config.url);

    if (promise) {
      return promise;
    } else {
      const promise = axios(config);
      store.setItem(config.url, promise);
      return promise;
    }
  }

  return axios(config);
};

class API {
  constructor(config) {
    this.defaultHeaders = [HEADERS_TOKEN, HEADERS_ACCEPT, HEADERS_APPLICATION, HEADERS_LANGUAGE];
    this.config = config;
  }

  getBaseUrl() {
    return this.config.base;
  }

  getApiUrl() {
    return this.config.api;
  }

  getAuthUrl() {
    return this.config.auth;
  }

  getToken() {
    return this.config.token;
  }

  getApplicationId() {
    return this.config.appId;
  }

  applicationHeader() {
    if (this.getApplicationId()) {
      return {
        'X-APPLICATION': this.getApplicationId()
      };
    }

    return {};
  }

  tokenHeader() {
    const token = this.getToken();

    if (token) {
      return {
        'Authorization': `Token ${this.getToken()}`
      };
    }

    return {};
  }

  acceptHeader() {
    return {
      'Accept': `application/json;`
    };
  }

  languageHeader() {
    const language = this.config.language || this.config.requestedLanguage || this.config.defaultLanguage || 'et';
    return {
      'Accept-Language': language
    };
  }

  getHeaders(requestHeaders, sendAuth) {
    let headers = {};

    if (this.defaultHeaders.indexOf(HEADERS_TOKEN) > -1 && sendAuth) {
      headers = { ...headers,
        ...this.tokenHeader()
      };
    }

    if (this.defaultHeaders.indexOf(HEADERS_ACCEPT) > -1) {
      headers = { ...headers,
        ...this.acceptHeader()
      };
    }

    if (this.defaultHeaders.indexOf(HEADERS_APPLICATION) > -1) {
      headers = { ...headers,
        ...this.applicationHeader()
      };
    }

    if (this.defaultHeaders.indexOf(HEADERS_LANGUAGE) > -1) {
      headers = { ...headers,
        ...this.languageHeader()
      };
    }

    if (requestHeaders) {
      headers = { ...headers,
        ...requestHeaders
      };
    }

    return headers;
  }

  call(url, method, data, params, requestHeaders = {}, config = {}, sendAuth = true) {
    const headers = this.getHeaders(requestHeaders, sendAuth);
    let request = { ...config,
      method: method,
      url: url
    };

    if (data) {
      request.data = data;
    }

    if (headers) {
      request.headers = headers;
    }

    if (params) {
      request.params = params;
    } // request.withCredentials =  true


    return cachedAxios(request);
  } // For calling routes in API


  callRoute(route, method, data, params, headers = {}, config = {}) {
    return this.call(this.getApiUrl() + route, method, data, params, headers, config);
  }

  login(username, password) {
    return this.call(this.getAuthUrl(), 'POST', {
      username,
      password
    }, null, {}, {}, false);
  }

  fetchList(route, params) {
    return this.callRoute(`${route}/`, 'GET', null, params);
  }

  fetchEntity(route, id, params) {
    return this.callRoute(`${route}/${id}/`, 'GET', null, params);
  }

  updateEntity(route, id, data) {
    return this.callRoute(`${route}/${id}/`, 'PATCH', data, null);
  }

  createEntity(route, data) {
    return this.callRoute(`${route}/`, 'POST', data, null);
  }

  deleteEntity(route, id, data) {
    return this.callRoute(`${route}/${id}/`, 'DELETE', data, null);
  }

  fetchOptions(route, id, params) {
    if (id) {
      return this.callRoute(`${route}/${id}/`, 'OPTIONS', null, params);
    } else {
      return this.callRoute(`${route}/`, 'OPTIONS', null, params);
    }
  }

  fetchRouteOptions(route, params) {
    return this.callRoute(`${route}/`, 'OPTIONS', null, params);
  }

  getUsers() {
    return this.callRoute('users/', 'GET');
  }

  uploadFile(data, progressHandler) {
    return this.callRoute('file-uploads/', 'POST', data, null, {}, {
      onUploadProgress: progressHandler
    });
  }

  orderAction(url, params) {
    return this.call(url, 'POST');
  }

  cartAction(url, data) {
    return this.call(url, 'POST', data);
  }

  paymentMethodAction(url, method = 'GET', params = null, data = null) {
    return this.call(url, method, data, params);
  }

  orderExport(filters, format, shopId) {
    let request = {
      method: 'POST',
      url: `${this.getBaseUrl()}order/export-orders/`,
      responseType: 'blob'
    };
    request.data = {
      filters: filters,
      format: format,
      token: this.getToken(),
      shop_id: shopId
    };
    return axios(request);
  }

}

var createSymbol = function createSymbol(name) {
  return "@@redux-saga/" + name;
};

var CANCEL =
/*#__PURE__*/
createSymbol('CANCEL_PROMISE');
var IO =
/*#__PURE__*/
createSymbol('IO');

var notUndef = function notUndef(v) {
  return v !== null && v !== undefined;
};
var func = function func(f) {
  return typeof f === 'function';
};
var string = function string(s) {
  return typeof s === 'string';
};
var array = Array.isArray;
var object = function object(obj) {
  return obj && !array(obj) && typeof obj === 'object';
};
var effect = function effect(eff) {
  return eff && eff[IO];
};

function delayP(ms, val) {
  if (val === void 0) {
    val = true;
  }

  var timeoutId;
  var promise = new Promise(function (resolve) {
    timeoutId = setTimeout(resolve, ms, val);
  });

  promise[CANCEL] = function () {
    clearTimeout(timeoutId);
  };

  return promise;
}

if (process.env.NODE_ENV !== 'production' && typeof Proxy !== 'undefined') ;
function check(value, predicate, error) {
  if (!predicate(value)) {
    throw new Error(error);
  }
}
var CALL = 'CALL';
var FORK = 'FORK';

var makeEffect = function makeEffect(type, payload) {
  var _ref;

  return _ref = {}, _ref[IO] = true, _ref.combinator = false, _ref.type = type, _ref.payload = payload, _ref;
};

var validateFnDescriptor = function validateFnDescriptor(effectName, fnDescriptor) {
  check(fnDescriptor, notUndef, effectName + ": argument fn is undefined or null");

  if (func(fnDescriptor)) {
    return;
  }

  var context = null;
  var fn;

  if (array(fnDescriptor)) {
    context = fnDescriptor[0];
    fn = fnDescriptor[1];
    check(fn, notUndef, effectName + ": argument of type [context, fn] has undefined or null `fn`");
  } else if (object(fnDescriptor)) {
    context = fnDescriptor.context;
    fn = fnDescriptor.fn;
    check(fn, notUndef, effectName + ": argument of type {context, fn} has undefined or null `fn`");
  } else {
    check(fnDescriptor, func, effectName + ": argument fn is not function");
    return;
  }

  if (context && string(fn)) {
    check(context[fn], func, effectName + ": context arguments has no such method - \"" + fn + "\"");
    return;
  }

  check(fn, func, effectName + ": unpacked fn argument (from [context, fn] or {context, fn}) is not a function");
};

function getFnCallDescriptor(fnDescriptor, args) {
  var context = null;
  var fn;

  if (func(fnDescriptor)) {
    fn = fnDescriptor;
  } else {
    if (array(fnDescriptor)) {
      context = fnDescriptor[0];
      fn = fnDescriptor[1];
    } else {
      context = fnDescriptor.context;
      fn = fnDescriptor.fn;
    }

    if (context && string(fn) && func(context[fn])) {
      fn = context[fn];
    }
  }

  return {
    context: context,
    fn: fn,
    args: args
  };
}

var isNotDelayEffect = function isNotDelayEffect(fn) {
  return fn !== delay;
};

function call(fnDescriptor) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  if (process.env.NODE_ENV !== 'production') {
    var arg0 = typeof args[0] === 'number' ? args[0] : 'ms';
    check(fnDescriptor, isNotDelayEffect, "instead of writing `yield call(delay, " + arg0 + ")` where delay is an effect from `redux-saga/effects` you should write `yield delay(" + arg0 + ")`");
    validateFnDescriptor('call', fnDescriptor);
  }

  return makeEffect(CALL, getFnCallDescriptor(fnDescriptor, args));
}
function apply(context, fn, args) {
  if (args === void 0) {
    args = [];
  }

  var fnDescriptor = [context, fn];

  if (process.env.NODE_ENV !== 'production') {
    validateFnDescriptor('apply', fnDescriptor);
  }

  return makeEffect(CALL, getFnCallDescriptor([context, fn], args));
}
function fork(fnDescriptor) {
  if (process.env.NODE_ENV !== 'production') {
    validateFnDescriptor('fork', fnDescriptor);
    check(fnDescriptor, function (arg) {
      return !effect(arg);
    }, 'fork: argument must not be an effect');
  }

  for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
    args[_key3 - 1] = arguments[_key3];
  }

  return makeEffect(FORK, getFnCallDescriptor(fnDescriptor, args));
}
var delay =
/*#__PURE__*/
call.bind(null, delayP);

let requestTask;

const getExpiresAtFromData = data => {
  let expiresAt = data.exp ? new Date(data.exp * 1000) : null;

  if (data.orig_iat) {
    const expiresIn = data.exp ? data.exp * 1000 - data.orig_iat * 1000 : null;
    expiresAt = expiresIn ? new Date(data.orig_iat * 1000 + expiresIn * 0.75) : null;
  }

  return expiresAt;
};

function* fetchToken(api) {
  try {
    const cuidValue = cuid();
    const response = yield apply(api, api.fetchCustomerToken, [cuidValue]); // get the token and JWT data

    const token = response.data.token;
    const data = JSON.parse(window.atob(token.split('.')[1]));
    const responseCuid = data.cuid ? data.cuid : null;

    if (responseCuid !== cuidValue) {
      throw new Error('Cuid Mismatch');
    } // calculate when the token expires (75% of what the API returns)


    const expiresAt = getExpiresAtFromData(data); // const expiresAt = expiresIn ? new Date(new Date() + expiresIn * 0.75) : null

    return {
      token,
      data,
      expiresAt
    };
  } catch (e) {
    console.log('error', e);
    return {};
  }
}

function* refreshToken(api, token) {
  try {
    const previousData = JSON.parse(window.atob(token.split('.')[1]));
    const cuidValue = previousData.cuid;
    const response = yield apply(api, api.refreshCustomerToken, [token]); // get the token and JWT data

    const newToken = response.data.token;
    const data = JSON.parse(window.atob(newToken.split('.')[1]));
    const responseCuid = data.cuid ? data.cuid : null;

    if (responseCuid !== cuidValue) {
      throw new Error('Cuid Mismatch');
    } // calculate when the token expires (75% of what the API returns)


    const expiresAt = getExpiresAtFromData(data); // const expiresAt = expiresIn ? new Date(new Date() + expiresIn * 0.75) : null

    return {
      newToken,
      data,
      expiresAt
    };
  } catch (e) {
    return yield call(fetchToken, api);
  }
}

class JWTAPI extends API {
  getCustomerTokenUrl() {
    return this.config.customerTokenUrls.obtain;
  }

  verifyCustomerTokenUrl() {
    return this.config.customerTokenUrls.verify;
  }

  refresCustomerTokenUrl() {
    return this.config.customerTokenUrls.refresh;
  }

  upgradeCustomerTokenUrl() {
    return this.config.customerTokenUrls.upgrade;
  }

  getCustomerToken() {
    return this.config.customerToken;
  }

  getUserToken() {
    return get(this.config, 'userTokens.accessToken');
  }

  getTokenExpiresAt() {
    const token = this.getCustomerToken();

    if (token) {
      const data = JSON.parse(window.atob(token.split('.')[1]));
      return getExpiresAtFromData(data);
    }

    return null;
  }

  fetchCustomerToken(cuid) {
    let headers = Object.assign({}, this.applicationHeader(), this.languageHeader());
    let request = {
      method: 'POST',
      url: this.getCustomerTokenUrl()
    };
    request.data = {
      cuid
    };

    if (headers) {
      request.headers = headers;
    } // request.withCredentials =  true


    return axios(request);
  }

  verifyCustomerToken(token) {
    let headers = Object.assign({}, this.applicationHeader(), this.languageHeader());
    let request = {
      method: 'POST',
      url: this.verifyCustomerTokenUrl()
    };
    request.data = {
      token
    };

    if (headers) {
      request.headers = headers;
    } // request.withCredentials =  true


    return axios(request);
  }

  refreshCustomerToken(token) {
    let headers = Object.assign({}, this.applicationHeader(), this.languageHeader());
    let request = {
      method: 'POST',
      url: this.refresCustomerTokenUrl()
    };
    request.data = {
      token
    };

    if (headers) {
      request.headers = headers;
    } // request.withCredentials =  true


    return axios(request);
  }

  userTokenHeader(token) {
    const userToken = token || this.getUserToken();

    if (userToken) {
      return {
        'Authorization': `Bearer ${userToken}`
      };
    }

    return {};
  }

  upgradeCustomerToken(token) {
    let headers = Object.assign({}, this.applicationHeader(), this.languageHeader(), this.userTokenHeader());
    let request = {
      method: 'POST',
      url: this.upgradeCustomerTokenUrl()
    };
    request.data = {
      token
    };

    if (headers) {
      request.headers = headers;
    } // request.withCredentials =  true


    return axios(request);
  }

  *requestToken(token) {
    let result;

    if (requestTask) {
      result = yield requestTask.done;
    } else {
      if (token) {
        requestTask = yield fork(refreshToken, this, token);
        result = yield requestTask.toPromise();
        requestTask = null;
      } else {
        requestTask = yield fork(fetchToken, this);
        result = yield requestTask.toPromise();
        requestTask = null;
      }
    }

    return result;
  }

  customerTokenHeader(token) {
    const customerToken = token || this.getCustomerToken();

    if (customerToken) {
      return {
        'Authorization': `JWT ${customerToken}`
      };
    }

    return {};
  }

  *call(url, method, data, params, requestHeaders = {}, config = {}, sendAuth = true, getNewToken = false) {
    let headers = Object.assign({}, this.applicationHeader(), this.languageHeader(), this.customerTokenHeader());
    let request = {
      method: method,
      url: url
    };
    let token = this.getCustomerToken();
    let newToken = false;
    const expiresAt = this.getTokenExpiresAt();

    if (!token || expiresAt && expiresAt < new Date() || getNewToken) {
      const result = yield apply(this, this.requestToken, [token]);
      token = result.token;
      newToken = true;
      headers = { ...headers,
        ...this.customerTokenHeader(token)
      };
    }

    if (data) {
      request.data = data;
    }

    if (headers) {
      request.headers = headers;
    }

    if (params) {
      request.params = params;
    } // request.withCredentials =  true


    let response;

    try {
      response = yield call(cachedAxios, request);
    } catch (e) {
      if (e.status === 401 && !getNewToken) {
        response = yield apply(this, this.call, [url, method, data, params, true]);
      } else {
        throw e;
      }
    }

    if (newToken) {
      response.newToken = token;
    }

    return response;
  }

}

export { API, JWTAPI as JWTApi };
